import { ApiCallModel, LineHandling } from "../models";
import { ImageAlignment, PDFDocument } from "pdf-lib";
import { AuthProvider } from "../AuthProvider";
import moment from "moment";
import { WorkPacketValue } from "../models/WorkPacket";
import { getInitials } from "../services/Helper";
import { StandardFonts } from "pdf-lib";

export const mergeImages = async (images: string[]) => {
  let x = 0;
  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  canvas.width = 500;
  const ctx = canvas.getContext("2d");

  for (var image of images) {
    await imageProcessor(image, x, ctx!);
    x += 100;
  }

  return canvas.toDataURL();
};

const imageProcessor = (
  src: string,
  xCoordinate: number,
  ctx?: CanvasRenderingContext2D
) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(ctx?.drawImage(img, xCoordinate, 0));
    img.onerror = reject;
    img.src = src;
  });
};

export const loadPdfDoc = async (
  layout: string,
  lineHandlingNo: string | undefined,
  nominationNo: string | undefined,
  documentId: number | undefined,
  lineHandling: ApiCallModel<LineHandling> | undefined,
  workPacketValues: ApiCallModel<WorkPacketValue[]>,
  customValues?: any
) => {
  const pdfFile = await fetch(layout, {
    headers: {
      Origin: window.location.hostname,
    },
  });
  const pdfByts = await pdfFile.arrayBuffer();
  try {
    const pdfDoc = await PDFDocument.load(pdfByts);

    const form = pdfDoc.getForm();
    const formFields = form.getFields();

    for (var formField of formFields) {
      const fieldName = formField.getName();
      
      formField.enableReadOnly();

      if (fieldName.startsWith("__initials__")) {
        const accountInfo = AuthProvider.getAccountInfo();
        const textField = form.getTextField(fieldName);
        textField.setText(
          accountInfo !== null ? getInitials(accountInfo.account.name) : ""
        );
        textField.enableReadOnly();
        continue;
      } else if (fieldName.startsWith("__line_handling_no__")) {
        const textField = form.getTextField(fieldName);
        textField.setText(lineHandlingNo);
        continue;
      } else if (fieldName.startsWith("__nomination_no__")) {
        const textField = form.getTextField(fieldName);
        textField.setText(nominationNo);
        continue;
      } else if (fieldName.startsWith("__vessel_name__")) {
        const textField = form.getTextField(fieldName);
        textField.setText(lineHandling?.result?.vesselName);
        continue;
      } else if (fieldName.startsWith("__current_date__")) {
        const date = moment(new Date());
        const textField = form.getTextField(fieldName);
        textField.setText(date.format("ll HH:mm"));
        continue;
      } else if (fieldName.startsWith("__signatories__")) {
        const users = workPacketValues.result
          ?.filter((f) => f.valueType === 7 && f.fieldValue !== "")
          .map((m) => m.createdByUser);
        var uniqueUsers = Array.from(new Set(users));
        const signatories = uniqueUsers?.join(", ");
        const textField = form.getTextField(fieldName);
        textField.setText(signatories);
        continue;
      } else if (fieldName.startsWith("customer_order_name")) {
        const textField = form.getTextField(fieldName);
        textField.setText(customValues?.customerOrderNo);
        continue;
      }

      const wpValue = workPacketValues.result?.find(
        (f) =>
          f.fieldName === fieldName.replace("!", "") &&
          f.documentId === documentId &&
          (f.fieldName.indexOf("#") >= 0 ||
            f.nominationNo?.toUpperCase() === nominationNo?.toUpperCase())
      );

      if (wpValue) {
        const value = wpValue.fieldValue;

        switch (wpValue.valueType) {
          case 1:
          case 6:
            const textField = form.getTextField(fieldName);
            if (value) {
              textField.setText(value);
              if (fieldName === "sampling_data") {
                const customFont = await pdfDoc.embedFont(
                  StandardFonts.Courier
                );
                textField.updateAppearances(customFont);
              }
            }
            break;
          case 2:
            const dateField = form.getTextField(fieldName);
            if (value) {
              const date = moment(new Date(value));
              dateField.setText(date.format("ll"));
            }
            break;
          case 3:
            const dateTimeField = form.getTextField(fieldName);
            if (value) {
              const dateTime = moment(new Date(value));
              dateTimeField.setText(dateTime.format("ll HH:mm"));
            }
            break;
          case 4:
            try {
              const booleanField = form.getTextField(fieldName);
              const bool = fieldName.startsWith("!")
                ? value !== "true"
                  ? "x"
                  : ""
                : value === "true"
                ? "x"
                : "";
              booleanField.setText(bool);
            } catch {
              const checkboxField = form.getCheckBox(fieldName);
              if (value === "true") {
                checkboxField.check();
              }
            }
            break;
          case 5:
            const imageField = form.getButton(fieldName);
            if (value && value.length > 0) {
              const pngImage = await pdfDoc.embedPng(value);

              const { height, width } = pngImage.size();
              //console.log({ height, width });
              let scaled: any;

              if (height > 100 || width > 100) {
                scaled = pngImage.scale(0.15);
              } else if (height > 45 || width > 45) {
                scaled = pngImage.scale(0.25);
              } else {
                scaled = pngImage.scale(0.4);
              }

              const widgets = imageField.acroField.getWidgets();

              widgets.forEach((w) => {
                const rect = w.getRectangle();
                const page = pdfDoc.getPages().find((p) => p.ref === w.P());
                //console.log(rect);

                page?.drawImage(pngImage, {
                  x: rect.x + rect.width / 3,
                  y: rect.y + 2,
                  width: scaled.width,
                  height: scaled.height,
                });
              });

              //imageField.setImage(pngImage, ImageAlignment.Center);
            }
            break;
          case 7:
            const imageMergedField = form.getButton(fieldName);
            const images = workPacketValues.result
              ?.filter(
                (f) =>
                  f.fieldName === fieldName.replace("!", "") &&
                  f.fieldValue !== ""
              )
              .map((m) => m.fieldValue);

            if (images?.length === 0) continue;

            const mergedImages = await mergeImages(images!);
            const pngImage = await pdfDoc.embedPng(mergedImages);

            const scaled = pngImage.scale(0.15);

            const widgets = imageMergedField.acroField.getWidgets();

            widgets.forEach((w) => {
              const rect = w.getRectangle();
              const page = pdfDoc.getPages().find((p) => p.ref === w.P());
              page?.drawImage(pngImage, {
                x: rect.x + rect.width / 3,
                y: rect.y - 3,
                width: scaled.width,
                height: scaled.height,
              });
            });

            //imageField.setImage(pngImage, ImageAlignment.Center);

            break;
          case 8:
            const imageMergedField2 = form.getButton(fieldName);
            if (value === undefined || value === null || value.trim() === "")
              break;
            const images2 = value.split("\r\n");
            if (images2.length === 0) continue;

            const mergedImages2 = await mergeImages(images2!);
            const pngImage2 = await pdfDoc.embedPng(mergedImages2);

            const scaled2 = pngImage2.scale(0.15);

            const widgets2 = imageMergedField2.acroField.getWidgets();

            widgets2.forEach((w) => {
              const rect = w.getRectangle();
              const page = pdfDoc.getPages().find((p) => p.ref === w.P());
              page?.drawImage(pngImage2, {
                x: rect.x + rect.width / 3,
                y: rect.y - 3,
                width: scaled2.width,
                height: scaled2.height,
              });
            });
            break;
          default:
            try {
              const textField = form.getTextField(fieldName);
              textField.setText("");
            } catch {
              try {
                const btnField = form.getButton(fieldName);
                btnField.enableReadOnly();
              } catch {}
            }
            break;
        }
      }
    }

    return pdfDoc;
  } catch (error) {}
};

export const mergedPdfs = async (documents: any) => {
  const mergedPdf = await PDFDocument.create();

  for (let document of documents) {
    document = await PDFDocument.load(document);

    const copiedPages = await mergedPdf.copyPages(
      document,
      document.getPageIndices()
    );
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  return await mergedPdf.saveAsBase64({ dataUri: true });
};

export const mergedPdfsAsBytes = async (documents: any) => {
  const mergedPdf = await PDFDocument.create();

  for (let document of documents) {
    document = await PDFDocument.load(document);

    const copiedPages = await mergedPdf.copyPages(
      document,
      document.getPageIndices()
    );
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  return await mergedPdf.save({});
};
